// frontend/src/services/api.ts

import axios from "axios";

// Types
export interface User {
  id: number;
  username: string;
  nome: string;
  cognome: string;
  isAdmin: boolean;
  canDownload: boolean;
}

export interface PatientData {
  id: number;
  nome: string;
  cognome: string;
  data_nascita: string;
  codice_fiscale: string;
  telefono: string;
  email: string;
  medico_base: string;
  gruppo_sanguigno: string;
  anamnesi_familiare: Array<{
    grado: string;
    patologia: string;
  }>;
  allergie: Array<{
    tipo: string;
    nome: string;
    note: string;
  }>;
  patologie: Array<{
    nome: string;
    data: string;
    stato: "In corso" | "Risolta";
    note: string;
  }>;
  terapie: Array<{
    farmaco: string;
    dosaggio: string;
    frequenza: string;
    inizioTerapia: string;
    prescrittore: string;
  }>;
  updated_at: string;
}

export interface Document {
  id: number;
  filename: string;
  title: string;
  type: "image" | "pdf";
  exam_date: string;
  tags: string[];
  is_visible: boolean;
  file_path?: string;
  URL?: string;
  alinkColor?: string;
  all?: any;
  anchors?: any;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: {
    code: string;
    message: string;
  };
}

// API Configuration
const API_URL = process.env.REACT_APP_API_URL
  ? `http://${process.env.REACT_APP_API_URL}`
  : "/api";

const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Required for cookies
});

// Add response interceptor for handling auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Only redirect if not already on login page
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = async (
  username: string,
  password: string
): Promise<ApiResponse<{ user: User }>> => {
  try {
    const response = await api.post<ApiResponse<{ user: User }>>(
      "/auth/login",
      {
        username,
        password,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const logout = async (): Promise<void> => {
  try {
    await api.post("/auth/logout");
  } catch (error) {
    throw error;
  }
};

// Documents
export const getDocument = async (
  id: number
): Promise<ApiResponse<Document>> => {
  try {
    const response = await api.get<ApiResponse<Document>>(`/documents/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDocuments = async (filters?: {
  search?: string;
  tags?: string[];
}): Promise<ApiResponse<Document[]>> => {
  try {
    const response = await api.get<ApiResponse<Document[]>>("/documents", {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Patient
export const getPatient = async (): Promise<ApiResponse<PatientData>> => {
  try {
    const response = await api.get<ApiResponse<PatientData>>("/patient");
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updatePatient = async (
  data: Partial<PatientData>
): Promise<ApiResponse<PatientData>> => {
  try {
    const response = await api.patch<ApiResponse<PatientData>>(
      "/patient",
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Document Download
export const downloadDocument = async (id: number): Promise<Blob> => {
  try {
    const response = await api.get(`/documents/${id}/download`, {
      responseType: "blob",
      headers: {
        Accept: "application/octet-stream",
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export default api;
